import React, { useState } from "react";
import { ListEvents } from "@/parts/list-events";
import { ViolationReport } from "@/components/ui/violation-report";
import { EventKind } from "../../api";
import { FusionLayout } from "@/parts/fusion-layout";
import {
    List,
    ListContent,
    ListDescription,
    ListFilters,
    ListHeader,
    ListSearchInput,
    ListTitle,
} from "@/components/ui/list";
import { useTranslation } from "react-i18next";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Download, Filter } from "lucide-react";
import { useDebounceValue } from "usehooks-ts";
import { useSearchParams } from "react-router";
import { useFetchEvents, useFetchEventsMetaData } from "@/lib/hooks/event";
import { usePageParam } from "@/lib/hooks/page-param";

export const PageIncidents = (): JSX.Element => {
    const [queryParams, setQueryParams] = useSearchParams();
    const incidentIdFromUrl = queryParams.get("incident");
    const [page, setPage] = usePageParam();

    const selectedEventId = incidentIdFromUrl ? incidentIdFromUrl : undefined;
    const setSelectedEventId = (eventId?: string): void => {
        if (eventId && eventId != selectedEventId) {
            setQueryParams((prev) => {
                prev.set("incident", eventId);
                return prev;
            });
        } else {
            setQueryParams((prev) => {
                prev.delete("incident");
                return prev;
            });
        }
    };

    const [eventKinds, setEventKinds] = useState<EventKind[]>([]);
    const [search, setSearch] = useDebounceValue("", 250);
    const { t } = useTranslation();

    const [events, _isEventsLoading, _, refreshEvents] = useFetchEvents(eventKinds, search, page);
    const [eventsMetaData, _isEventsMetaDataLoading] = useFetchEventsMetaData(eventKinds, search);

    const selectedEvent = selectedEventId
        ? events?.find((event) => event.id === selectedEventId)
        : undefined;

    function handleFilterChange(checked: boolean, eventKind: EventKind): void {
        setEventKinds(
            (prev) =>
                checked
                    ? [...prev, eventKind] // Add if the item is checked.
                    : prev.filter((kind) => kind !== eventKind), // Remove if the item is not checked.
        );
    }

    function handleSearchChange(change: React.ChangeEvent<HTMLInputElement>): void {
        setSearch(change.currentTarget.value);
    }

    const eventsFilter = (
        <ListFilters>
            <ListSearchInput onChange={handleSearchChange} />
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button className="font-semibold" variant="outline">
                        <Filter />
                        {t("component.list.filters")}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                    {Object.values(EventKind).map((eventKind, index) => {
                        return (
                            <DropdownMenuCheckboxItem
                                key={index}
                                checked={eventKinds.includes(eventKind)}
                                onCheckedChange={(checked) =>
                                    handleFilterChange(checked, eventKind)
                                }
                            >
                                {t(`schema.event.kind.${eventKind}`)}
                            </DropdownMenuCheckboxItem>
                        );
                    })}
                </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="outline" disabled>
                <Download />
            </Button>
        </ListFilters>
    );

    return (
        <FusionLayout breadcrumb="Incidents">
            <div className="flex h-full flex-col">
                <div className="flex w-full grow gap-4 overflow-hidden">
                    <List className="w-[50%]">
                        <ListHeader>
                            <div>
                                <ListTitle>{t("listEvents.title")}</ListTitle>
                                <ListDescription>{t("listEvents.description")}</ListDescription>
                            </div>
                        </ListHeader>
                        <ListContent className="h-full">
                            {eventsFilter}
                            <ListEvents
                                selectedEventId={selectedEventId}
                                onSelectEvent={(e) => setSelectedEventId(e?.id)}
                                setPage={setPage}
                                page={page}
                                events={events ?? []}
                                refreshEvents={refreshEvents}
                                eventsMetaData={eventsMetaData}
                            />
                        </ListContent>
                    </List>
                    <ViolationReport className="w-[50%]" selectedEvent={selectedEvent} />
                </div>
            </div>
        </FusionLayout>
    );
};
